import { useCallback, useEffect, useMemo } from 'react';
// @Mui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
} from '@mui/material';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/system/Unstable_Grid/Grid';
// components
import FormProvider, { BlockItem, RHFTextField } from 'src/components/hook-form';
import { changePassword } from 'src/api/account';
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';

import { handleError } from './formUtil';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  onLogout: VoidFunction;
};

export default function DoiMatKhau({ open, onClose, onLogout }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const showOldPassword = useBoolean();
  const showNewPassword = useBoolean();

  const showConfirmNewPassword = useBoolean();

  const Schema = Yup.object().shape({
    oldPassword: Yup.string()
      .required('Bạn chưa nhập mật khẩu hiện tại !')
      .min(6, 'Mật khẩu ít nhất 6 kí tự')
      .max(255, 'Tối đa 255 kí tự'),
    newPassword: Yup.string()
      .required('Bạn chưa nhập mật khẩu mới !')
      .min(6, 'Mật khẩu ít nhất 6 kí tự')
      .max(255, 'Tối đa 255 kí tự'),
    confirmNewPassword: Yup.string()
      .required('Bạn chưa nhập xác nhận mật khẩu mới !')
      .oneOf([Yup.ref('newPassword')], 'Mật khẩu không trùng khớp với mật khẩu mới !')
      .min(6, 'Mật khẩu ít nhất 6 kí tự')
      .max(255, 'Tối đa 255 kí tự'),
  });

  const defaultValues = useMemo(() => {
    return {
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    };
  }, []);

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      await changePassword(data.oldPassword, data.newPassword);

      enqueueSnackbar('Thay đổi mật khẩu thành công !');
      onLogout();
      handleClose();
    } catch (error) {
      handleError(error, setError);
    }
  });
  const handleClose = useCallback(() => {
    onClose();
    reset();
    showOldPassword.onFalse();
    showNewPassword.onFalse();
    showConfirmNewPassword.onFalse();
  }, [onClose]);

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { maxWidth: 720 },
      }}
      sx={{
        '& form > *': {
          px: {
            xs: 2,
            md: 3,
          },
        },
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>Thay đổi mật khẩu</DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <BlockItem label="Mật khẩu hiện tại : " required>
                <RHFTextField
                  size="small"
                  name="oldPassword"
                  type={showOldPassword.value ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={showOldPassword.onToggle} edge="end">
                          <Iconify
                            icon={
                              showOldPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </BlockItem>
            </Grid>
            <Grid xs={12}>
              <BlockItem label="Mật khẩu mới : " required>
                <RHFTextField
                  size="small"
                  name="newPassword"
                  type={showNewPassword.value ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={showNewPassword.onToggle} edge="end">
                          <Iconify
                            icon={
                              showNewPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </BlockItem>
            </Grid>
            <Grid xs={12}>
              <BlockItem label="Nhập lại mật khẩu mới : " required>
                <RHFTextField
                  size="small"
                  name="confirmNewPassword"
                  type={showConfirmNewPassword.value ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={showConfirmNewPassword.onToggle} edge="end">
                          <Iconify
                            icon={
                              showConfirmNewPassword.value
                                ? 'solar:eye-bold'
                                : 'solar:eye-closed-bold'
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </BlockItem>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Huỷ bỏ
          </Button>

          <LoadingButton type="submit" color="primary" variant="contained" loading={isSubmitting}>
            Đổi mật khẩu
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
